if (!customElements.get('product-overview')) {
  class ProductOverview extends HTMLElement {
    static observedAttributes = ['data-enable-sticky-atb', 'data-enable-sticky-atb-on'];

    constructor() {
      super();
      this.baseUrl = window.Shopify.routes.root + 'products/';
      this.elements = {};
      this.selectedOptions = [];
      this.selectedValues = [];
      this.promotion = {};
      this.boundHandleStickyATC = this.handleStickyATC.bind(this);
      this.boundHandleResize = this.handleResize.bind(this);
      this.atbButtonMarginOffset = 150;
    }

    async connectedCallback() {
      this.enableStickyATB = this.dataset.enableStickyAtb == "true" ? true : false;
      this.enableStickyATBon = this.dataset.enableStickyAtbOn || 'render';
      await this.init();
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) return;

      switch(name) {
        case 'data-enable-sticky-atb':
          this.enableStickyATB = newValue === "true";
          break;
        case 'data-enable-sticky-atb-on':
          this.enableStickyATBon = newValue || 'scroll';
          break;
      }

      this.toggleStickyATBFunctionality();
    }

    // simple debug class that is called for every console.log
    // just add the class and the debug logs roll in
    allowDebug = () => {
      return (this.classList.contains("debug"));
    }

    /**
     *  Set up data, elements, and events.
     *  Dispatch an initialized event on completion
     */
    init = async () => {
      try {
        if(this.allowDebug()) console.log('product-overview.js: init()', this);
        this.product = this.setProduct();
        if(this.allowDebug()) console.log('product-overview.js: product', this.product);
        this.noVariant = false;
        this.variant = this.setDefaultVariant();
        if(this.allowDebug()) console.log('product-overview.js: variant', this.variant);
        this.setElements();
        this.setEventListeners();
        if(this.product?.available){
          this.setSelectedSwatchData();
          await this.updateState();
        }
        this.dispatchEvent(new CustomEvent('product-overview:initialized', {
          bubbles: true,
          detail: {
            productOverview: this,
            product: this.product,
            variant: this.variant
          }
        }));
      } catch(error) {
        console.error('Error initializing component:', error, this);
      }
    }

    /**
     * Component Setters
     */

    setProduct = () => {
      this.JSONScriptTag = this.querySelector(`script[type="application/json"]:first-of-type`);
      const tempData = this.JSONScriptTag ? JSON.parse(this.JSONScriptTag.innerHTML) : {};
      // always scrub the JSON from the front end, we will take what we get at render and use this as the source of truth.
      if(this.JSONScriptTag) this.removeChild(this.JSONScriptTag);
      if(this.allowDebug()) console.log('product-overview.js: setProduct()', tempData, this);
      return tempData;
    }

    setDefaultVariant = () => {
      if(this.product?.is_parent_product){
        return this.getVariantById(this.product?.default_child_variant_id);
      } else {
        return this.getVariantById(this.product?.default_variant_id);
      }
    }

    setElements = () => {
      this.elements.productImages = this.querySelector('.js-product-images');
      this.elements.productMediaContainer = this.querySelector('.product__slider-container');
      this.elements.form = this.querySelector('.js-product-form');
      this.elements.formHead = this.querySelector('.js-form-head');
      this.elements.priceElement = this.querySelector('.js-price');
      this.elements.priceElementMobile = this.querySelector('.js-price-mobile');
      this.promotionElements = this.querySelectorAll('product-promotion');
      this.elements.colorElement = this.querySelector('.js-color');
      this.elements.swatchContainer = this.querySelector('.js-product-swatches');
      this.elements.swatches = this.querySelectorAll('.js-product-swatches input');
      this.elements.optionGroups = this.querySelectorAll('.js-product-radios');
      this.elements.nonColorGroups = this.querySelectorAll('.js-product-radios:not(.js-product-swatches)');
      this.elements.delimitedOptions = this.querySelectorAll('.js-product-delimited-option');
      this.elements.delimitedOptionGroups = this.querySelectorAll('.js-product-delimited-radios');
      this.elements.atcButton = this.querySelector('button[data-add-to-cart]');
      this.elements.preorderMessage = this.querySelector('.js-product__po_message');
      this.elements.preorderMessageProperty = this.querySelector('input[name="properties[_po_message]"]');
      this.elements.minicartMessage = this.querySelector('.js-product__minicart_message');
      this.elements.minicartMessageProperty = this.querySelector('input[name="properties[_minicart_message]"]');
      this.elements.productActions = this.querySelector('.product__form-actions');
      this.elements.reviewStarsLinks = this.querySelectorAll('.product__reviews-stars-link');
    }

    setEventListeners = () => {
      // sticky ATC button
      if (this.enableStickyATB) {
        window.addEventListener('scroll', this.boundHandleStickyATC);
      }

      this.elements.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submitToCart(event);
      });

      this.elements.reviewStarsLinks?.forEach(link => {
        link.addEventListener('click', (event) => {
          event.preventDefault();
          this.scrollToReviews(event.target);
        });
      });

      this.addEventListener('change', async (event) => {
        const target = event.target;

        // Handle delimited option changes
        const delimitedGroup = target.closest('.js-product-delimited-radios');
        if (delimitedGroup && target.classList.contains('js-variant-delimited-option')) {
          event.preventDefault();

          // First check if this is an impossible selection
          if (target.classList.contains('impossible-combination')) {
            this.variant = {};
            this.setUrl();
            this.updateOptionsAvailablility();
            return;
          }

          const delimitedOption = delimitedGroup.dataset.option;
          const result = this.calculateDelimitedOption(delimitedOption);

          if (!result.valid) {
            // If not valid, clear the variant and update URL
            this.variant = {};
            this.setUrl();
            this.updateOptionsAvailablility();
            return;
          }

          await this.updateState();
          return;
        }

        // Handle standard option changes
        if (target.closest('.js-product-radios:not(.js-product-swatches)')) {
          event.preventDefault();
          const selectedTitle = this.getSelectedOptionsTitle();
          this.variant = this.getVariantByTitle(selectedTitle);
          await this.updateState();
          return;
        }

        // Handle swatch changes
        if (target.closest('.js-product-radios.js-product-swatches')) {
          event.preventDefault();
          this.handleSwatchChange(target);
          return;
        }
      });

      this.toggleStickyATBFunctionality();
      this.addEventListener('product-promotion:change', this.handlePromotionChange);
    }

    handleSwatchChange = async (swatch) => {
      if(!swatch) {
        if(this.allowDebug()) console.log("No swatch data sent");
        return;
      }
      if(this.isParentProduct()){
        this.getChildProductFromSwatch(swatch);
      } else {
        // Find the matching swatch object based on data-js-handle attribute
        const handleizedColor = swatch.dataset.jsHandle;
        const matchingSwatch = this.product.swatches?.find((swatch) => swatch.handle === handleizedColor);
        if (!matchingSwatch) {
          if(this.allowDebug()) console.log("No matching swatch found");
          return;
        }
        this.selectedSwatchData = matchingSwatch;
        this.selectedSwatchElement = swatch;
        const selectedTitle = this.getSelectedOptionsTitle();
        this.variant = this.getVariantByTitle(selectedTitle);
        await this.updateProductMedia();
        this.updateColorLabel();
        await this.updateState();
        this.dispatchEvent(new CustomEvent('product-overview:swatch-change', {
          bubbles: true,
          detail: {
            data: this.selectedSwatchData,
            element: this.selectedSwatchElement,
            variant: this.variant
          }
        }));
      }

    }

    setSelectedSwatchData = () => {
      // Start with an empty state.
      this.selectedSwatchElement = null;
      this.selectedSwatchData = {};
      if(!this.hasSwatches()){
        if(this.allowDebug()) console.log('product-overview.js: setSelectedSwatchData(), no swatches for the given section.', this);
        return;
      }
      // Select the currently selected swatch element.
      const selectedSwatchElement = this.elements.swatchContainer.querySelector("input:checked");
      if (!selectedSwatchElement){
        if(this.allowDebug()) console.log('product-overview.js: setSelectedSwatchData(), unable to find default selected swatch in the DOM.', this);
        /*
          If we cannot find a selected swatch, find the first input and click it.
          Exit this function, the regular click handler will deal with this.
        */
        const firstSwatch = this.elements.swatchContainer.querySelector("input:first-of-type");
        if(firstSwatch) firstSwatch.click();
        return;
      }
      this.selectedSwatchElement = selectedSwatchElement;
      const handleizedColor = selectedSwatchElement.dataset.jsHandle;
      const matchingSwatch = this.product.swatches?.find((swatch) => swatch.handle === handleizedColor);
      if (!matchingSwatch){
        if(this.allowDebug()) console.log('product-overview.js: setSelectedSwatchData(), unable to find default selected swatch in the DOM.', this);
        return;
      }
      this.selectedSwatchData = matchingSwatch;
      this.updateColorLabel();
    }

    setUrl = () => {
      let urlParams = {};
      const locationUrl = window.location
      // allow existing params to persist in the URL. Code below will re-write any necessary values relating to the component.
      const params = new URLSearchParams(locationUrl.search);
      for (const [key, value] of params) {
        urlParams[key] = value;
      }
      // if we have no variant, clear the URL, update the ATC button
      if(Object.keys(this.variant).length == 0){
        this.noVariant = true;
        this.updateATCButton(false, "Product not available");
        // if we are not calling this function on a PDP, we can early exit.
        if(!window.location.href.includes(`/products/${this.product?.handle}`)) return;
        // clear the URL, default it to the current path.
        let newUrl = `${locationUrl.pathname}`;
        // replace the history state
        history.replaceState({}, '', newUrl);
        return;
      }
      if(this.isParentProduct()) {
        if(this.selectedSwatchElement){
          urlParams.variant = this.selectedSwatchElement.dataset.jsVariantId;
          urlParams.child_product = this.selectedSwatchElement.dataset.jsChildProductId;
          urlParams.child_variant = this.variant.id;
        }
      } else {
        urlParams.variant = this.variant.id;
      }
      // at this point let's update the ATC button based on current variant data
      // set add to cart state based on Shopify's availability
      this.noVariant = false;
      this.updateATCButton(this.variant.available_shopify);
      // if we are not calling this function on a PDP, skip it.
      if(!window.location.href.includes(`/products/${this.product?.handle}`)) return;
      // create a new URL by combining the path and the urlParams
      let newUrl = `${locationUrl.pathname}?${new URLSearchParams(urlParams)}`;
      history.replaceState({}, '', newUrl);
    }

    updateState = async () => {
      if(this.allowDebug()) console.log(`product-oveview.js updateState`, this.variant);
      this.setUrl();

      this.updateOptionsAvailablility();

      // Reset state if no valid variant found
      if (Object.keys(this.variant).length === 0) {
        const selectedTitle = this.getSelectedOptionsTitle();
        this.updateInventoryAlert("");
        this.updatePreorderText("");
        this.updateMiniCartMessageInput("");
        if(this.allowDebug()) console.log(`product-overview.js unable to find variant under title ${selectedTitle}`, this.product.variants);
        // tell the world the variant here changed
        this.dispatchEvent(new CustomEvent('product-overview:no-variant', {
          bubbles: true,
          detail: {
            productOverview: this,
            requestedVariantTitle: selectedTitle
          }
        }));
        return;
      }

      // Update UI
      this.setPrice(this.variant.price_html);
      this.updateSale(this.variant.on_sale);
      this.updateFinalSale(this.variant.is_final_sale);
      this.updateInventoryAlert(this.variant.inventory_alert_message);
      this.updatePreorderText(this.variant.show_preorder_message);
      this.updateMiniCartMessageInput(this.variant.show_minicart_message);
      this.updateVariantSelectionOnButtons();

      if(this.promotionElements) {
        try {
          await Promise.all(Array.from(this.promotionElements).map(async (promotionElement) => {
            const promotionFetchData = {
              url: this.variant.url
            };

            if (this.product.is_parent_product) {
              promotionFetchData.url = this.variant.parent_variant_url;
              promotionFetchData.backup = {
                url: this.variant.child_variant_url
              };
            }

            await promotionElement.updateFetchData(promotionFetchData);
          }));
        } catch (error) {
          console.error('Error updating promotions:', error);
        }
      }

      // tell the world the variant here changed
      this.dispatchEvent(new CustomEvent('product-overview:variant-change', {
        bubbles: true,
        detail: {
          productOverview: this,
          variant: this.variant
        }
      }));
    }

    /**
     * Component Getters
     */

    getProduct = () => {
      return this.product ?? {};
    }

    hasSwatches = () => {
      return this.product.swatches?.length > 0;
    }

    isStandardProduct = () => {
      return this.product.is_standard_product ?? false;
    }

    isParentProduct = () => {
      return this.product.is_parent_product ?? false;
    }

    isChildProduct = () => {
      return this.product.is_child_product ?? false;
    }

    isSingleVariantProduct = () => {
      return this.product.is_single_variant_product ?? false;
    }

    getPriceRangeHTML = () => {
      return this.product.price_range_html ?? null;
    }

    getVariants = () => {
      return this.product.variants ?? [];
    }

    getSelectedSwatchElement = () => {
      return this.selectedSwatchElement ?? null;
    }

    getSelectedSwatchData = () => {
      return this.selectedSwatchData ?? {};
    }

    getVariantByTitle = (title) => {
      if (!this.product?.variants) return {};
      return this.product.variants.find(variant => window.theme.utilities.handleize(variant.title) === window.theme.utilities.handleize(title) ) ?? {};
    }

    getVariantById = (id) => {
      if (!this.product?.variants) return {};
      return this.product.variants.find(variant => variant.id === id) ?? {};
    }

    getVariant = () => {
      return this.variant;
    }

    getSelectedOptions = () => {
      return this.querySelectorAll(".js-variant-option:checked");
    }

    getSelectedOptionsTitle = () => {
      this.selectedOptions = this.getSelectedOptions();
      this.selectedValues = Array.from(this.selectedOptions).map(selectedOption => {
        return selectedOption.dataset.title;
      });
      return this.selectedValues.join(" / ");
    }

    getFetchUrl = (handle) => {
      let dataUrl = `${handle}`;
      if(this.isParentProduct()) {
        if(this.selectedSwatchElement){
          dataUrl += `?variant=${this.selectedSwatchElement.dataset.jsChildVariantId}`;
        }
      } else {
        dataUrl += `?variant=${this.variant.id}`;
      }
      dataUrl += `&view=product-layout&fetch=true`;
      return dataUrl;
    }

    /**
     * Component Updaters
     */

    updateATCButton = (variantAvailible, overrideText) => {
      if(!this.elements.atcButton) return;
      const atcButtonText = this.elements.atcButton.querySelector('.button__text');
      atcButtonText.innerText = this.elements?.atcButton?.dataset?.availableText;
      if(variantAvailible) {
        atcButtonText.innerText = this.elements.atcButton.dataset.availableText;
        this.elements.atcButton.removeAttribute('disabled');
        this.elements.atcButton.removeAttribute('aria-disabled');
      } else {
        atcButtonText.innerText = this.elements.atcButton.dataset.unavailableText;
        this.elements.atcButton.setAttribute('disabled', true);
        this.elements.atcButton.setAttribute('aria-disabled', true);
      }
      if(overrideText){
        atcButtonText.innerText = overrideText;
      }
      if(this.product.third_parties.loop_returns_eligible == false && window.LoopOnstore?.isActive() == true){
        atcButtonText.innerText = `This product is not eligible for Loop On-Store Returns`;
        this.elements.atcButton.setAttribute('disabled', true);
        this.elements.atcButton.setAttribute('aria-disabled', true);
      }
      if(this.noVariant){
        this.elements.atcButton.classList.add('no-variant');
      } else {
        this.elements.atcButton.classList.remove('no-variant');
      }
    }

    updateColorLabel = () => {
      if(!this.elements.colorElement) return;
      if(Object.keys(this.selectedSwatchData).length == 0) return;
      this.elements.colorElement.innerHTML = `${window.theme.utilities.capitalize(this.selectedSwatchData.name?.toLowerCase())} <span>${window.theme.utilities.capitalize(this.selectedSwatchData.color_family?.toLowerCase())}</span>`;
    }

    updateOptions = (html) => {
      const optionsHtml = html?.querySelector('.js-product-options');
      if(!optionsHtml) return;
      // before replacing content, get the current selected options so we can try to deep link.
      const currentOptionGroups = this.querySelectorAll(".js-product-radios:not(.js-product-swatches)");
      let currentSelection = {};
      currentOptionGroups.forEach((group) => {
        const selectedRadio = group.querySelector('input[type="radio"]:checked');
        if (selectedRadio) {
          currentSelection[group.dataset.optionKey] = selectedRadio.value;
        }
      });
      // grab the new HTML we want to inject, and inject it.
      const optionsArea = this.querySelector('.js-product-options');
      optionsArea.innerHTML = optionsHtml.innerHTML;
      // re-define elements
      this.elements.optionGroups = this.querySelectorAll('.js-product-radios');
      this.elements.nonColorGroups = this.querySelectorAll('.js-product-radios:not(.js-product-swatches)');
      // Try to re-select previously selected options in the new HTML.
      Object.keys(currentSelection).forEach((optionKey) => {
        const valueToSelect = currentSelection[optionKey];
        const newOptionGroup = optionsArea.querySelector(`.js-product-radios[data-option-key="${optionKey}"]`);
        const matchingRadio = newOptionGroup.querySelector(`input[type="radio"][value="${valueToSelect}"]`);
        if (matchingRadio) {
          // Uncheck the currently selected radio in the group.
          const currentlySelected = newOptionGroup.querySelector('input[type="radio"]:checked');
          if (currentlySelected) {
            currentlySelected.checked = false;
          }
          // Check the matching radio.
          matchingRadio.checked = true;
        }
      });
    }

    updatePreorderText = (showPreorderMessage) => {
      /*
        If the variant intends to show a preorder message, unhide the messaging and enable the line item property
      */
      if(showPreorderMessage){
        this.elements.preorderMessage?.classList.remove("hidden");
        this.elements.preorderMessageProperty?.removeAttribute("disabled");
      } else {
        this.elements.preorderMessage?.classList.add("hidden");
        this.elements.preorderMessageProperty?.setAttribute('disabled', true);
      }
    }

    updateMiniCartMessageInput = (enableMinicartMessage) => {
      /*
        if the variant intends to show a minicart message, enable the line item property otherwise disable
      */
      if(enableMinicartMessage){
        this.elements.minicartMessageProperty?.removeAttribute("disabled");
      } else {
        this.elements.minicartMessageProperty?.setAttribute('disabled', true);
      }
    }

    // update the price elements
    setPrice = (price) => {
      if(!price) return;
      if(!this.elements.priceElement) return;
      this.elements.priceElement.innerHTML = price;
      this.elements.priceElementMobile.innerHTML = price;
    }

    setSale = (bool) => {
      this.updateSale(bool);
    }

    updateSale = (show) => {
      const saleProperties = this.querySelectorAll('.sale-property');
      const finalSaleProperties = this.querySelectorAll('.final-sale-property');
      if(show) {
        saleProperties.forEach((prop) => prop.removeAttribute('disabled'));
        finalSaleProperties.forEach((prop) => prop.setAttribute('disabled', true));
      } else {
        saleProperties.forEach((prop) => prop.setAttribute('disabled', true));
      }
    }

    setFinalSale = (bool) => {
      this.updateFinalSale(bool);
    }

    setInventoryAlert = (html) => {
      this.updateInventoryAlert(html);
    }

    updateInventoryAlert = (html) => {
      const inventoryAlertContainer = this.querySelector('.product__inventory-alert-container');
      if(!inventoryAlertContainer) return;
      inventoryAlertContainer.innerHTML = html;
    }

    updateFinalSale = (show) => {
      const saleProperties = this.querySelectorAll('.sale-property');
      const finalSaleProperties = this.querySelectorAll('.final-sale-property');
      if(show) {
        finalSaleProperties.forEach((prop) => prop.removeAttribute('disabled'));
        saleProperties.forEach((prop) => prop.setAttribute('disabled', true));
      } else {
        finalSaleProperties.forEach((prop) => prop.setAttribute('disabled', true));
      }
    }

    updateVariantSelectionOnButtons = () => {
      if(!this.elements.atcButton || !this.elements.productActions) return;

      let selectedVariantTitle = "";

      if(Object.keys(this.variant).length > 0 && this.variant?.title) {
        // First replace all ' / ' with ' - ' and handle Wolf case
        selectedVariantTitle = this.variant.title.replaceAll(' / ', ' - ').replaceAll('-Wolf-', ' - ');
        // Split by ' - ' to check if last two parts are numbers
        const parts = selectedVariantTitle.split(' - ');
        if (parts.length >= 2) {
          // Check if last two parts are numbers
          const lastTwo = parts.slice(-2);
          if (lastTwo.every(part => !isNaN(part.trim()))) {
            // Replace last occurrence of ' - ' with ' x '
            // Split at the last ' - ' and restitch the string.
            const lastDashIndex = selectedVariantTitle.lastIndexOf(' - ');
            if (lastDashIndex !== -1) {
              selectedVariantTitle = `${selectedVariantTitle.substring(0, lastDashIndex)} x ${selectedVariantTitle.substring(lastDashIndex + 3)}`;
            }
          }
        }
      }

      const atcButtonTexts = this.elements.productActions.querySelectorAll('.button__text');

      atcButtonTexts.forEach((atcButtonText) => {
        const existingSpan = atcButtonText?.querySelector('.atc-variant-info');
        if (existingSpan) {
          existingSpan.textContent = selectedVariantTitle;
        } else {
          let span = document.createElement('span');
          span.className = 'atc-variant-info medium-hide large-up-hide';
          span.textContent = selectedVariantTitle;
          atcButtonText?.append(span);
        }
      });
    }

    updateProductMedia = async () => {
      if(!this.elements.productMediaContainer) return;
      const media_url = this.variant?.media_url ?? this.selectedSwatchData?.media_url;
      if(this.allowDebug()) console.log(`Fetching swatch media from ${media_url}`);
      if(!media_url) return;
      try {
        const response = await fetch(media_url);
        if (!response.ok) throw new Error('Failed to fetch media content');

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newMediaComponent = doc.querySelector('product-media');

        if (!newMediaComponent) {
          throw new Error('No media component found in response');
        }

        // Clean up existing component
        const oldMediaComponent = this.elements.productMediaContainer.querySelector('product-media');
        if (oldMediaComponent?.addedScrollEvent) {
          const scrollContainer = oldMediaComponent.closest('.quick-add-modal__content') || window;
          const boundUpdateIndicators = oldMediaComponent.updateIndicators.bind(oldMediaComponent);
          scrollContainer.removeEventListener('scroll', boundUpdateIndicators);
        }

        // Replace the component
        this.elements.productMediaContainer.innerHTML = '';
        this.elements.productMediaContainer.appendChild(newMediaComponent);

        this.dispatchEvent(new CustomEvent('product-overview:product-media-updated', {
          bubbles: true,
          detail: {
            variant: this.variant,
            mediaComponent: newMediaComponent
          }
        }));

      } catch (error) {
        console.error('Error updating product media:', error);
        this.dispatchEvent(new CustomEvent('product-overview:product-media-error', {
          bubbles: true,
          detail: { error: error.message }
        }));
      }
    }

    getChildProductFromSwatch = async (swatch) => {
      if(this.allowDebug()) console.log('product-overview.js getChildProductFromSwatch', swatch);
      try {
        await Promise.all([
          this.getChildProductData(swatch.dataset.jsChildProductUrl),
        ]);
        const selectedTitle = this.getSelectedOptionsTitle();
        this.variant = this.getVariantByTitle(selectedTitle);
        await this.updateProductMedia();
        this.updateColorLabel();
        await this.updateState();
        this.dispatchEvent(new CustomEvent('product-overview:swatch-change', {
          bubbles: true,
          detail: {
            data: this.selectedSwatchData,
            element: this.selectedSwatchElement,
            variant: this.variant
          }
        }));
      } catch(error){
        console.error(error);
      }
    }

    setAddingToBagState = (isAdding) => {
      const button = this.elements.atcButton;
      if (!button) return;
      const buttonText = button.querySelector('.button__text');
      const spinner = button.querySelector('.loading-overlay__spinner');
      button.classList.toggle('adding-to-bag', isAdding);
      button.toggleAttribute('disabled', isAdding);
      button.toggleAttribute('aria-disabled', isAdding);
      button.classList.toggle('loading', isAdding);
      if (buttonText) {
        buttonText.innerText = isAdding ? "Adding to bag" : button.dataset.availableText;
      }
      if (spinner) {
        spinner.classList.toggle('hidden', !isAdding);
      }
      if(!isAdding) {
        this.updateVariantSelectionOnButtons();
      }
    }

    submitToCart = async (event) => {
      event.preventDefault();
      // if the ATC button is not present on the page, we do not want to allow submission.
      if(!this.elements.atcButton) return;
      if(Object.keys(this.variant).length == 0) return;
      try{
        const form = this.elements.form;
        const action = form.getAttribute('action') + '.js';
        const variantId = this.variant.id;

        let properties = {};

        // search the page for any hidden inputs that relate to line item properties
        this.elements.form.querySelectorAll(`input[type="hidden"][name^="properties["]:not(:disabled)`).forEach(el => {
          const propertyName = el.name.match(/\[([^)]+)\]/)?.[1];
          if (propertyName) properties[propertyName] = el.value;
        });

        // Collect promotion properties
        if(this.promotion && Object.keys(this.promotion).length > 0) {
          const promotionProperties = window.theme.utilities.getPromotionProperties(this.promotion);
          if (Object.keys(promotionProperties).length > 0) {
            // Go through the promotionProperties return and add each property entry to the properties object
            Object.entries(promotionProperties).forEach(([key, value]) => {
              properties[`${key}`] = value.value;
            });
          }
        }

        if(this.variant.color) {
          properties['_color_id'] = this.variant.color;
        }

        const atcItems = [{ id: variantId, quantity: 1, properties: properties }]
        const requestBody = { items: atcItems, sections: 'cart-drawer' };

        this.setAddingToBagState(true);

        await fetch(action, {
          method: 'POST',
          body: JSON.stringify(requestBody),
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          }})
          .then(res => res.json())
          .then((data) => {
            const quickview = this.closest('quickview-modal');
            quickview && quickview.close();

            const currentCartDrawer = document.querySelector(`cart-drawer`);

            if(currentCartDrawer) {
              currentCartDrawer.refresh();
              currentCartDrawer.open();
            }

            this.setAddingToBagState(false);

            this.dispatchEvent(new CustomEvent('product-overview:add-to-cart', {
              bubbles: true,
              detail: { items: atcItems }
            }));
          })
          .catch(error => console.error(error));
        } catch(error){
          console.error(error);
        }
    }

    scrollToReviews = (clickedLink) => {
      const starsParentMobile = clickedLink.closest('.product-info-mobile');
      let reviewsContent;

      // Open the relevant reviews accordion or tab
      if (starsParentMobile) {
        // Mobile stars were clicked, open the reviews accordion:
        reviewsContent = document.querySelector('product-accordion')?.querySelector('#product-tab-reviews');
        const reviewsAccordion = reviewsContent?.closest('details');
        reviewsAccordion?.classList.add('smooth-scroll');
        reviewsAccordion?.setAttribute('open', true);
      } else {
        // Desktop stars were clicked, click the reviews tab:
        reviewsContent = document.querySelector('product-tabs')?.querySelector('#product-tab-reviews');
        const reviewsTab = document.querySelector('[data-target="#product-tab-reviews"]');
        reviewsTab?.click();
      }

      // Scroll to the reviews content
      if (reviewsContent) {
        const getContentOffset = () => {
          const totalHeaderHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height') || '0', 10);
          const breadcrumbs = document.querySelector('.shopify-section.product-breadcrumbs');
          const breadcrumbsHeight = breadcrumbs && getComputedStyle(breadcrumbs).position === 'sticky'
            ? breadcrumbs.offsetHeight
            : 0;
          return totalHeaderHeight + breadcrumbsHeight;
        };

        const offsetPosition = (reviewsContent?.getBoundingClientRect().top ?? 0) + window.scrollY - getContentOffset();

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }

    fetchChildProductData = async (handle) => {
      const fetch_url = this.getFetchUrl(handle);
      try{
        const response = await fetch(fetch_url);
        const responseHTML = await response.text();
        return responseHTML;
      } catch (error) {
        console.error(error);
      }
    }

    getChildProductData = async (handle) => {
      if(this.allowDebug()) console.log('product-overview.js getChildProductData', handle);
      if(window.outerWidth > 767) {
        window.scrollTo({top: 0, left: 0, behavior: 'instant'});
      }
      const childProductResponse = await this.fetchChildProductData(handle);
      const responseHTML = new DOMParser().parseFromString(childProductResponse, 'text/html');
      if(responseHTML){
        const childProductOverview = responseHTML.querySelector('product-overview');
        this.updateOptions(childProductOverview);
        this.setElements();
        this.setSelectedSwatchData();
      }
      return Promise.resolve();
    }

    handleStickyATBChange = () => {
      const newEnableStickyATB = this.getAttribute('data-enable-sticky-atb') == "true" ? true : false;
      if (newEnableStickyATB != this.enableStickyATB) {
        this.enableStickyATB = newEnableStickyATB;
        this.toggleStickyATBFunctionality();
      }
    }

    toggleStickyATBFunctionality = () => {
      if (this.enableStickyATB) {
        this.enableStickyATBEvents();
        this.handleStickyATC();
      } else {
        this.disableStickyATBEvents();
      }
    }

    enableStickyATBEvents = () => {
      window.addEventListener('scroll', this.boundHandleStickyATC);
      window.addEventListener('resize', this.boundHandleResize);
      this.handleStickyATC();
    }

    disableStickyATBEvents = () => {
      window.removeEventListener('scroll', this.boundHandleStickyATC);
      window.removeEventListener('resize', this.boundHandleResize);
      this.resetStickyATBStyles();
    }

    resetStickyATBStyles = () => {
      if (this.elements.productActions) {
        this.elements.productActions.classList.remove('product__form-actions--sticky', 'product__form-actions--sticky-stop');
      }
    }

    handleStickyATC = () => {
      if (!this.elements.productActions || !this.enableStickyATB) return;

      const mainContent = document.getElementById('MainContent');
      if (!mainContent) return;

      const mainContentRect = mainContent.getBoundingClientRect();
      const buttonWrapperRect = this.elements.productActions.getBoundingClientRect();
      const scrollTop = window.scrollY;
      const totalHeaderHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 92;

      // First check if we should be sticky at all based on scroll position
      const shouldBeSticky = scrollTop > (buttonWrapperRect.top + scrollTop - totalHeaderHeight);

      if (!shouldBeSticky) {
        this.elements.productActions.classList.remove('product__form-actions--sticky', 'product__form-actions--sticky-stop');
        return;
      }

      // Then check main content visibility
      const mainContentBottom = mainContentRect.bottom;
      const viewportHeight = window.innerHeight;

      if (mainContentBottom <= viewportHeight) {
        this.elements.productActions.classList.add('product__form-actions--sticky-stop');
        this.elements.productActions.classList.remove('product__form-actions--sticky');
      } else {
        this.elements.productActions.classList.add('product__form-actions--sticky');
        this.elements.productActions.classList.remove('product__form-actions--sticky-stop');
      }
    }

    handleResize = () => {
      this.handleStickyATC();
    }

    limitStickyATCScroll = () => {
      const pageFooter = document.getElementById('shopify-section-footer');
      if (!pageFooter || !this.elements.productActions) return;
      const topOfFooter = pageFooter.getBoundingClientRect().top;
      if (topOfFooter <= window.innerHeight + this.atbButtonMarginOffset) {
        this.elements.productActions.classList.add('product__form-actions--sticky-stop');
      } else {
        this.elements.productActions.classList.remove('product__form-actions--sticky-stop');
      }
    }

    handlePromotionChange = (event) => {
      // Check if the event's triggering element is a descendant of this component
      if (this.contains(event.detail.triggeringElement)) {
        this.promotion = event.detail.promotion;
      }
    }

    selectTrueFitSize = (sizes) => {
      if (!sizes || !this.querySelectorAll('.js-product-radios').length) return;

      const sizeGroup = this.querySelector('.js-product-radios[data-option="Size"]');
      if (!sizeGroup) {
        return; // Early exit if no size group found
      }

      // normalize recommended size values
      const size_suggested = window.theme.utilities.handleize(sizes.suggested);
      const size_alternative = window.theme.utilities.handleize(sizes.alternative);

      const sizeInputs = sizeGroup.querySelectorAll('input');
      sizeInputs.forEach((sizeInput) => {
        // normalize size input value
        const size_input_value = window.theme.utilities.handleize(sizeInput.value);
        if (size_input_value === size_suggested || size_input_value === size_alternative) {
          sizeInput.checked = true;
          sizeInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    calculateDelimitedOption = (delimitedOption) => {
      if (!delimitedOption) return { valid: false };

      // Find the delimited option group container
      const container = this.querySelector(
        `.js-product-delimited-option[data-option="${delimitedOption}"]`
      );
      if (!container) return { valid: false };

      // Collect all selected parts
      const selectedParts = new Map();
      container.querySelectorAll('.js-variant-delimited-option:checked').forEach(selected => {
        const group = selected.closest('.js-product-delimited-radios');
        if (group?.dataset.optionMap) {
          selectedParts.set(group.dataset.optionMap, selected.value);
        }
      });

      if (this.allowDebug()) {
        console.log(`calculateDelimitedOption: collected parts for ${delimitedOption}:`, selectedParts);
      }

      // Find variant option that matches all selected parts
      const matchingVariantOption = Array.from(container.querySelectorAll('.js-variant-option'))
        .find(option => Array.from(selectedParts.entries())
          .every(([attr, value]) => option.getAttribute(attr) === value)
        );

      if (!matchingVariantOption) {
        if (this.allowDebug()) {
          console.log(`calculateDelimitedOption: no matching complete option found for ${delimitedOption}`);
        }
        return { valid: false };
      }

      // Update selection if necessary
      const currentlySelected = container.querySelector('.js-variant-option:checked');
      if (currentlySelected !== matchingVariantOption) {
        if (currentlySelected) {
          currentlySelected.checked = false;
        }
        matchingVariantOption.checked = true;
      }

      return { valid: true };
    }

    generateTitleFromSelection = (selectionState) => {
      // Get all option groups in order (by their optionKey which will be option1, option2, option3)
      const optionGroups = Array.from(this.elements.optionGroups)
        .sort((a, b) => {
          const aKey = parseInt(a.dataset.optionKey.replace('option', ''));
          const bKey = parseInt(b.dataset.optionKey.replace('option', ''));
          return aKey - bKey;
        });

      const values = [];

      // Go through each group in order
      optionGroups.forEach(group => {
        const isDelimited = group.classList.contains('js-product-delimited-option');

        if (isDelimited) {
          // For delimited options, find the selected complete variant option
          const selectedVariantOption = group.querySelector('.js-variant-option:checked');
          if (selectedVariantOption) {
            values.push(selectedVariantOption.value);
          }
        } else {
          // For standard options, use the value from selectionState
          const optionKey = group.dataset.optionKey;
          if (selectionState[optionKey]) {
            values.push(selectionState[optionKey]);
          }
        }
      });

      return values.join(' / ');
    }

    updateOptionsAvailablility = () => {
      // Get current standard option selections
      let currentSelection = {};
      const standardOptionGroups = Array.from(this.elements.optionGroups)
        .filter(group => !group.classList.contains('js-product-delimited-option'));

      standardOptionGroups.forEach((group) => {
        const selectedRadio = group.querySelector('input[type="radio"]:checked');
        if (selectedRadio) {
          currentSelection[group.dataset.optionKey] = selectedRadio.value;
        }
      });

      // Get all selected delimited options
      const delimitedValues = new Map();
      this.elements.delimitedOptions?.forEach(delimitedOption => {
        const selectedOption = delimitedOption.querySelector('.js-variant-option:checked');
        if (selectedOption) {
          delimitedValues.set(delimitedOption.dataset.optionKey, selectedOption.value);
        }
      });

      // Update standard options availability
      standardOptionGroups.forEach((group) => {
        const radios = group.querySelectorAll('input[type="radio"]');
        const optionKey = group.dataset.optionKey;

        radios.forEach((radio) => {
          // Create a complete test selection including this option
          const testSelection = { ...currentSelection, [optionKey]: radio.value };

          // Generate the title that would result from this combination
          const testTitle = this.generateTitleFromSelection(testSelection);

          // Check if any variant exists with this exact title
          const exactVariantExists = this.product.variants.some(variant =>
            window.theme.utilities.handleize(variant.title) === window.theme.utilities.handleize(testTitle)
          );

          const isImpossible = !exactVariantExists;
          const isAvailable = !isImpossible && this.product.variants.some(variant => {
            return window.theme.utilities.handleize(variant.title) === window.theme.utilities.handleize(testTitle) && variant.available_shopify;
          });

          this.updateOptionClasses(radio, isImpossible, !isAvailable);
        });
      });

      // Update delimited options availability
      this.elements.delimitedOptions?.forEach(delimitedOption => {
        const allDelimitedParts = delimitedOption.querySelectorAll('.js-variant-delimited-option');
        const variantOptions = Array.from(delimitedOption.querySelectorAll('.js-variant-option'));
        const optionKey = delimitedOption.dataset.optionKey;

        allDelimitedParts.forEach(option => {
          const group = option.closest('.js-product-delimited-radios');
          const groupMapping = group?.dataset.optionMap;
          if (!groupMapping) return;

          // Get current delimited selections for this option group
          const currentDelimitedSelections = new Map();
          delimitedOption.querySelectorAll('.js-variant-delimited-option:checked').forEach(selected => {
            const selectedGroup = selected.closest('.js-product-delimited-radios');
            if (selectedGroup !== group) {
              currentDelimitedSelections.set(selectedGroup.dataset.optionMap, selected.value);
            }
          });
          currentDelimitedSelections.set(groupMapping, option.value);

          // Find matching complete option
          const matchingVariantOption = variantOptions.find(variantOption => {
            return Array.from(currentDelimitedSelections.entries()).every(([attr, value]) => {
              return variantOption.getAttribute(attr) === value;
            });
          });

          if (matchingVariantOption) {
            // Test the complete combination
            const testSelection = { ...currentSelection };
            testSelection[optionKey] = matchingVariantOption.value;

            const testTitle = this.generateTitleFromSelection(testSelection);

            const exactVariantExists = this.product.variants.some(variant =>
              window.theme.utilities.handleize(variant.title) === window.theme.utilities.handleize(testTitle)
            );

            const isAvailable = exactVariantExists && this.product.variants.some(variant =>
              window.theme.utilities.handleize(variant.title) === window.theme.utilities.handleize(testTitle) && variant.available_shopify
            );

            this.updateOptionClasses(option, !exactVariantExists, !isAvailable);
          } else {
            // No matching complete option found - this is an impossible combination
            this.updateOptionClasses(option, true, false);
          }
        });
      });

      this.dispatchEvent(new CustomEvent('product-overview:option-availability-change', {
        bubbles: true
      }));
    }

    // Helper to update option classes consistently
    updateOptionClasses(element, impossible, unavailable) {
      element.classList.toggle('impossible-combination', impossible);
      element.classList.toggle('option-unavailable', unavailable && !impossible);

      if (impossible) {
        element.setAttribute('tabindex', '-1');
      } else {
        element.removeAttribute('tabindex');
      }
    }

  }

  customElements.define('product-overview', ProductOverview);
}

